import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { useSceneStore } from "@/state/useSceneStore";
import { useProgressStore } from "@/state/useProgressStore";
import { useFps } from "@/hooks/useFps";

const PULSE_SPEED = 0.02;
const BASE_INTENSITY = 0.12;
const PULSE_AMPLITUDE = 0.08;
const AUTO_HIDE_MS = 4000;

const setEmissive = (child: THREE.Object3D, intensity: number): void => {
  if (!(child instanceof THREE.Mesh)) return;
  const mat = child.material as THREE.MeshStandardMaterial | undefined;
  if (mat && "emissiveIntensity" in mat) mat.emissiveIntensity = intensity;
};

const formatUptime = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

export const Cpu = (): JSX.Element | null => {
  const mesh = useSceneStore((s) => s.objects.cpu);
  const quality = useSceneStore((s) => s.quality);
  const blurbOpen = useSceneStore((s) => s.cpuBlurbOpen);
  const toggleCpuBlurb = useSceneStore((s) => s.toggleCpuBlurb);
  const totalActiveSeconds = useProgressStore((s) => s.totalActiveSeconds);
  const fps = useFps();
  const phase = useRef(0);
  const autoHide = useRef<number | null>(null);

  useFrame(() => {
    if (!mesh) return;
    phase.current += PULSE_SPEED;
    const pulse = (Math.sin(phase.current) + 1) * 0.5;
    const intensity = BASE_INTENSITY + pulse * PULSE_AMPLITUDE;
    mesh.children.slice(0, 2).forEach((child) => setEmissive(child, intensity));
  });

  useEffect(() => {
    if (!blurbOpen) {
      if (autoHide.current !== null) {
        window.clearTimeout(autoHide.current);
        autoHide.current = null;
      }
      return;
    }
    autoHide.current = window.setTimeout(() => {
      toggleCpuBlurb();
      autoHide.current = null;
    }, AUTO_HIDE_MS);
    return () => {
      if (autoHide.current !== null) {
        window.clearTimeout(autoHide.current);
        autoHide.current = null;
      }
    };
  }, [blurbOpen, toggleCpuBlurb]);

  if (!mesh || !blurbOpen) return null;

  const worldPos = new THREE.Vector3();
  mesh.getWorldPosition(worldPos);
  worldPos.y += 0.6;

  return (
    <Html position={[worldPos.x, worldPos.y, worldPos.z]} zIndexRange={[10, 0]}>
      <div className="pointer-events-none -translate-x-1/2 -translate-y-full rounded-sm border border-phosphor bg-bg1/90 px-3 py-2 font-mono text-xs text-phosphor backdrop-blur-sm">
        <div className="uppercase tracking-wider text-ink2">system</div>
        <div>
          {fps} fps · gpu: {quality} · up {formatUptime(totalActiveSeconds)}
        </div>
        <div className="text-ink2">three 0.184 · R3F 9 · WebGL2</div>
      </div>
    </Html>
  );
};
